import LegalLayout, { LegalSection, LegalSubSection } from '@/components/LegalLayout';
import { Link } from 'react-router-dom';
import SeoHead from '@/components/SeoHead';

export default function Safeguarding() {
  return (
    <>
      <SeoHead
        title="Safeguarding Policy"
        description="WriteAssess's commitment to safeguarding the welfare of children and handling pupil data responsibly."
        canonical="/safeguarding"
      />
      <LegalLayout title="Safeguarding Policy" lastUpdated="26 October 2026">
      <LegalSection title="1. Our Commitment">
        <p>
          WriteAssess is committed to safeguarding the welfare of children. Although WriteAssess is a
          teacher-facing platform and pupils do not have direct access to it, we recognise that we process pupil
          data — including images of their handwritten work — and that this carries safeguarding responsibilities.
        </p>
        <p>
          This policy sets out the measures we take to protect children's data and welfare in the context of our
          platform, and the responsibilities of schools using our service.
        </p>
      </LegalSection>

      <LegalSection title="2. Platform Access">
        <LegalSubSection title="2.1 Teachers and administrators only">
          <p>
            WriteAssess accounts are issued only to qualified teachers, teaching assistants, and school
            administrators. The platform must not be used by or made accessible to pupils or parents. Schools are
            responsible for ensuring that login credentials are not shared with or accessible to pupils.
          </p>
        </LegalSubSection>
        <LegalSubSection title="2.2 School verification">
          <p>
            While we do not carry out formal DBS checks on individual users, school administrators are responsible
            for ensuring that only authorised staff are invited to use the platform. All accounts require email
            verification before access is granted.
          </p>
        </LegalSubSection>
      </LegalSection>

      <LegalSection title="3. Pupil Data Protections">
        <LegalSubSection title="3.1 No direct pupil contact">
          <p>
            WriteAssess does not contact pupils directly. We do not collect pupils' email addresses, phone numbers,
            or any contact details. All communication is with the teacher or school administrator.
          </p>
        </LegalSubSection>
        <LegalSubSection title="3.2 Writing samples">
          <p>
            Writing sample images are stored in a private, access-controlled storage bucket. They are not publicly
            accessible and cannot be viewed by anyone outside the school account that uploaded them. Images are
            accessible only to authenticated users within the same school.
          </p>
          <p>
            Teachers should ensure that uploaded images contain only the pupil's written work. Images should not
            include photographs of pupils' faces, other children, or any materials that could identify a vulnerable
            pupil in a harmful context.
          </p>
        </LegalSubSection>
        <LegalSubSection title="3.3 Anonymisation">
          <p>
            WriteAssess supports three levels of pupil name display: full name, first initial only, or
            initials only. Schools in sensitive contexts (e.g. where pupil identity must be protected) are
            encouraged to use an anonymised display setting.
          </p>
        </LegalSubSection>
        <LegalSubSection title="3.4 AI processing">
          <p>
            Pupil writing images are transmitted to OpenAI's API (GPT-6 Luna model) for analysis. OpenAI's API does not
            use submitted data to train models (confirmed via their enterprise data terms). Pupil names are not
            included in the prompts used to transcribe and grade writing — only the writing image and year group
            information. A name written on the page itself is visible in the image, so we recommend covering it
            before photographing the work. Report comment generation sends the pupil's display name so the comment
            can refer to them; if your school uses an anonymised display setting, that is what is sent.
          </p>
        </LegalSubSection>
      </LegalSection>

      <LegalSection title="4. School Responsibilities">
        <p>Schools using WriteAssess are responsible for:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>
            Informing their Data Protection Officer (DPO) and ensuring use of WriteAssess is included in their
            Records of Processing Activities (ROPA)
          </li>
          <li>
            Carrying out a Data Protection Impact Assessment (DPIA) if required under their school's policies
          </li>
          <li>
            Notifying parents/guardians as required under their school's privacy notice about the use of AI tools
            in the assessment process
          </li>
          <li>
            Ensuring uploaded writing samples do not include images of pupils' faces or other sensitive content
          </li>
          <li>
            Ensuring that access to WriteAssess is restricted to authorised staff and that credentials are kept
            secure
          </li>
          <li>
            Reporting any suspected data breaches involving WriteAssess to us and to their DPO without delay
          </li>
        </ul>
      </LegalSection>

      <LegalSection title="5. Reporting Concerns">
        <LegalSubSection title="5.1 Concerns about a child">
          <p>
            WriteAssess is not a safeguarding referral platform. If, through use of our platform, a teacher becomes
            aware of a concern about the welfare of a child (for example, through content visible in a writing
            sample), they must follow their school's safeguarding procedures and report to their Designated
            Safeguarding Lead (DSL) immediately.
          </p>
          <p>
            Concerns about child welfare should not be reported to WriteAssess — they must be escalated through the
            school's DSL to the appropriate statutory agencies (e.g. children's social care, police).
          </p>
        </LegalSubSection>
        <LegalSubSection title="5.2 Concerns about our platform">
          <p>
            If you have concerns about how WriteAssess handles pupil data or believe there has been a safeguarding
            failure on our part, contact us immediately at{' '}
            <Link to="/contact" className="text-primary hover:underline">our contact form</Link>. We aim to respond within one business day.
          </p>
        </LegalSubSection>
      </LegalSection>

      <LegalSection title="6. Regulatory Framework">
        <p>This policy is informed by and consistent with:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Keeping Children Safe in Education (KCSiE) — DfE statutory guidance</li>
          <li>UK GDPR and the Data Protection Act 2018</li>
          <li>ICO Age Appropriate Design Code (Children's Code)</li>
          <li>Working Together to Safeguard Children — DfE statutory guidance</li>
        </ul>
      </LegalSection>

      <LegalSection title="7. Policy Review">
        <p>
          This policy is reviewed at least annually and updated as required following changes to statutory guidance,
          legislation, or our platform features.
        </p>
      </LegalSection>

      <LegalSection title="8. Contact">
        <p>
          Safeguarding concerns relating to our platform:{' '}
          <Link to="/contact" className="text-primary hover:underline">our contact form</Link>
          <br />
          General data protection enquiries:{' '}
          <Link to="/contact" className="text-primary hover:underline">our contact form</Link>
        </p>
        <p>
          See also:{' '}
          <Link to="/data-protection" className="text-primary hover:underline">Data Protection Policy</Link>
          {' · '}
          <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link>
        </p>
      </LegalSection>
    </LegalLayout>
    </>
  );
}
